import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Кнопка согласия принимает нажатие не раньше, чем через столько после появления или сдвига (как диалоги разрешений
 * браузеров, ~0,5–1 с): сотрудник видит курсор пользователя и выбирает момент запроса — щелчок, начатый по кнопке,
 * которая стояла на этом месте раньше, согласием не становится.
 */
export const CONSENT_ARMING_DELAY = 700;

/** Сдвиг кнопки меньше этого (субпиксельная перекладка) — не сдвиг. */
const SHIFT_TOLERANCE = 1;

export interface IConsentArming {
    /** Нажатие уже засчитывается */
    armed: boolean;
    /** Узел, по сдвигу которого отсчёт начинается заново (кнопки согласия или их ряд) */
    ref: (node: HTMLElement | null) => void;
    /** Обработчик, который до срока ничего не делает */
    guard: <A extends unknown[]>(handler: (...args: A) => void) => (...args: A) => void;
}

/**
 * Задержка активации кнопок согласия (IMPLEMENTATION.md §40, ревью F4). `key` — что спрашивают (id запроса, фаза);
 * `null` — спрашивать нечего. Новый `key`, сдвиг узла `ref` (над ним появилась плашка, страница сменила раскладку) и
 * смена размера окна начинают отсчёт заново.
 */
export function useConsentArming(key: unknown): IConsentArming {
    const [armed, setArmed] = useState(false);
    const [node, setNode] = useState<HTMLElement | null>(null);
    // Нажатие сверяется с ним, а не с состоянием: отсчёт заново гасит кнопку ещё до перерисовки.
    const armedRef = useRef(false);

    useLayoutEffect(() => {
        const arm = (value: boolean) => {
            armedRef.current = value;
            setArmed(value);
        };
        if (key === null || key === undefined || key === false) {
            arm(false);
            return undefined;
        }
        let timer: ReturnType<typeof setTimeout> | undefined;
        const disarm = () => {
            clearTimeout(timer);
            arm(false);
            timer = setTimeout(() => arm(true), CONSENT_ARMING_DELAY);
        };
        disarm();
        if (!node) return () => clearTimeout(timer);
        let last = node.getBoundingClientRect();
        const check = () => {
            const next = node.getBoundingClientRect();
            if (
                Math.abs(next.left - last.left) < SHIFT_TOLERANCE &&
                Math.abs(next.top - last.top) < SHIFT_TOLERANCE &&
                Math.abs(next.width - last.width) < SHIFT_TOLERANCE
            )
                return;
            last = next;
            disarm();
        };
        // Плашки над кнопками и перекладка страницы меняют размер `body` (или самого ряда кнопок).
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
        observer?.observe(document.body);
        observer?.observe(node);
        window.addEventListener('resize', check);
        window.addEventListener('scroll', check, true);
        return () => {
            clearTimeout(timer);
            observer?.disconnect();
            window.removeEventListener('resize', check);
            window.removeEventListener('scroll', check, true);
        };
    }, [key, node]);

    const guard = useCallback(
        <A extends unknown[]>(handler: (...args: A) => void) =>
            (...args: A) => {
                if (armedRef.current) handler(...args);
            },
        [],
    );
    return { armed, ref: setNode, guard };
}
